import { joiResolver } from "@hookform/resolvers/joi";
import { Select } from "flowbite-react";
import Joi from "joi";
import { useForm } from "react-hook-form";
import useAuth from "../../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { FaX } from "react-icons/fa6";
import { HiOutlineUserGroup } from "react-icons/hi";
import {
  useAddUserToSpecifiedGroup,
  useDeleteUserFromGroup,
  useGetGroups,
} from "../../../../hooks/groups";
import { UserGroup } from "./ViewEntityUser";
import { CustomButton } from "../../../atoms/CustomButton";
import { EmptyState } from "../../../atoms/EmptyState";
import NoTranslate from "../../../atoms/NoTranslate";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

type ManageCustomerGroupsType = {
  data: UserGroup[] | [];
  user_identification: string;
  customerId: string;
  entity_identification: string;
};

const schema = Joi.object({
  group_identification: Joi.string().trim().required().label("Group"),
});

const AddUserToGroupForm = ({
  customerId,
}: {
  customerId: string | undefined;
}) => {
  const [t] = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canAddToGroup = permissions.can(Cap.GROUP_MANAGEMENT, Level.WRITE);

  const { register, handleSubmit } = useForm({
    resolver: joiResolver(schema),
  });

  const { data: groupsRes } = useGetGroups({
    customer_identification: customer_identification,
    entityIdentification: user.entity_identification,
    limit: 50,
    sortOrder: "DESC",
    pageParam: 0,
    enabled: permissions.can(Cap.GROUP_MANAGEMENT, Level.VIEW),
  });

  const { mutate, isLoading } = useAddUserToSpecifiedGroup();

  const onSubmit = (values: any) => {
    mutate({
      customer_identification: customer_identification,
      entityIdentification: user.entity_identification,
      customerId: customerId,
      group_identification: values.group_identification,
    });
  };

  if (!canAddToGroup) return null;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="self-stretch justify-start items-start gap-3 flex">
        <Select
          translate="no"
          aria-label="ownerGroups"
          className="w-full"
          {...register("group_identification")}
        >
          <option value="">
            {t("dashboard.entities.manageCustomerGroups.selectText")}
          </option>
          {groupsRes?.groups
            .filter((x: any) => x.group_name !== "BUSINESS CARDS")
            .map((gr: any, index: number) => (
              <option value={gr.group_identification} key={index}>
                {gr.group_name}
              </option>
            ))}
        </Select>

        <CustomButton
          variant="primary"
          type="submit"
          disabled={
            groupsRes?.groups.length <= 0 || isLoading || !canAddToGroup
          }
          isLoading={isLoading}
          title={t("common.add")}
        />
      </div>
    </form>
  );
};

const ManageCustomerGroups = ({
  data,
  customerId,
  user_identification,
  entity_identification,
}: ManageCustomerGroupsType) => {
  const [t] = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canRemoveFromGroup = permissions.can(Cap.GROUP_MANAGEMENT, Level.WRITE);

  const { mutate } = useDeleteUserFromGroup();

  const handleRemoveGroupFromUser = (groupIdentification: string) => {
    mutate({
      customer_identification: user_identification,
      entityIdentification: entity_identification,
      group_identification: groupIdentification,
      customerId: customerId,
    });
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 pb-3 mb-4">
        <h3 className="text-sm font-semibold text-gray-900">
          {t("dashboard.entities.manageCustomerGroups.title")}
        </h3>
        {canRemoveFromGroup && (
          <div className="w-full sm:ml-auto sm:w-80">
            <AddUserToGroupForm customerId={customerId} />
          </div>
        )}
      </div>

      {data.length > 0 ? (
        <div className="justify-start items-start gap-2 inline-flex flex-wrap">
          {data.map((g: UserGroup, index: number) => (
            <div
              key={index}
              className="px-2.5 py-0.5 bg-gray-100 rounded-xl justify-center items-center gap-0.5 flex"
            >
              <div className="text-center text-gray-800 text-sm font-medium font-figtree leading-tight">
                <NoTranslate>{g.group_name}</NoTranslate>
              </div>

              {canRemoveFromGroup && (
                <button
                  aria-label={
                    t("dashboard.entities.manageCustomerGroups.remove", {
                      group: g.group_name,
                    }) as string
                  }
                  onClick={() =>
                    handleRemoveGroupFromUser(g.group_identification)
                  }
                >
                  <FaX size={11} className="text-gray-400" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<HiOutlineUserGroup size={24} />}
          title={t("dashboard.entities.manageCustomerGroups.empty")}
        />
      )}
    </div>
  );
};

export default ManageCustomerGroups;
